/**
 * v145.27 — StorefrontCartBar
 * ---------------------------
 * v145.30 — Rendered INLINE at the bottom of the products grid so it
 * scrolls naturally with the page (no more `fixed` overlay). Also
 * accepts `deliveryCharge` from the block config and threads it into
 * the checkout modal.
 */
import { useState } from "react";
import { createPortal } from "react-dom";
import { ShoppingBag, X } from "lucide-react";
import { useStorefrontCart } from "./StorefrontCartContext";
import ProductThumb from "./ProductThumb";
import CartCheckoutModal from "./CartCheckoutModal";
import { useAuth } from "../../context/AuthContext";

export default function StorefrontCartBar({ slug, buyerUser, requirePhone, requireAddress, deliveryCharge = 0, codEnabled = false, onlineEnabled = true }) {
  const cart = useStorefrontCart();
  const { user } = useAuth();
  const effectiveUser = buyerUser || user || null;
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  if (!cart || cart.count === 0) return null;

  const dc = Math.max(0, Number(deliveryCharge) || 0);
  const drawerTotal = cart.subtotal + dc;

  return (
    <>
      <div
        className="mt-4 w-full border-4 border-ink bg-white shadow-brutal p-3 flex items-center gap-3"
        data-testid="storefront-cart-bar"
      >
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          className="flex items-center gap-2 flex-1 text-left px-1 min-w-0"
          data-testid="storefront-cart-open"
        >
          <div className="w-10 h-10 border-2 border-ink bg-sun inline-flex items-center justify-center font-black shrink-0">
            <ShoppingBag className="w-4 h-4" strokeWidth={2.5} />
          </div>
          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-wider">Cart · {cart.count} items</p>
            <p className="text-base font-black tabular-nums leading-tight">₹{cart.subtotal.toFixed(0)}</p>
          </div>
        </button>
        <button
          type="button"
          onClick={() => setCheckoutOpen(true)}
          className="border-2 border-ink bg-coral text-white px-4 py-2.5 font-black text-sm shadow-brutalSm shrink-0"
          data-testid="storefront-cart-checkout"
        >
          Checkout
        </button>
      </div>
      {drawerOpen && typeof document !== "undefined" && createPortal(
        <div
          className="fixed inset-0 z-[9998] bg-black/60 flex items-end sm:items-center justify-center p-3"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setDrawerOpen(false); }}
          data-testid="storefront-cart-drawer"
        >
          <div
            className="bg-white border-4 border-ink shadow-brutal w-full max-w-md p-4 space-y-3 max-h-[80dvh] overflow-y-auto"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="font-black text-lg">Your cart</h3>
              <button type="button" onClick={() => setDrawerOpen(false)} className="p-1" data-testid="storefront-cart-drawer-close">
                <X className="w-5 h-5" strokeWidth={2.5} />
              </button>
            </div>
            <div className="space-y-2">
              {cart.list.map((i) => (
                <div key={i.product.product_id} className="border-2 border-ink p-2 flex items-center gap-2" data-testid={`storefront-cart-item-${i.product.product_id}`}>
                  <ProductThumb
                    src={i.product.image_url}
                    name={i.product.title}
                    width={160}
                    className="w-12 h-12 border border-ink shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold truncate">{i.product.title}</p>
                    <p className="text-xs ah-muted tabular-nums">₹{Number(i.product.price || 0).toFixed(0)} each</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button type="button" onClick={() => cart.setUnits(i.product.product_id, i.units - 1)}
                            className="w-7 h-7 border-2 border-ink font-black" data-testid={`cart-dec-${i.product.product_id}`}>
                      −
                    </button>
                    <span className="w-8 text-center font-black tabular-nums">{i.units}</span>
                    <button type="button" onClick={() => cart.setUnits(i.product.product_id, i.units + 1)}
                            className="w-7 h-7 border-2 border-ink font-black" data-testid={`cart-inc-${i.product.product_id}`}>
                      +
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t-2 border-ink pt-2 space-y-1">
              <div className="flex justify-between text-sm">
                <span>Subtotal</span>
                <span className="tabular-nums">₹{cart.subtotal.toFixed(0)}</span>
              </div>
              {dc > 0 && (
                <div className="flex justify-between text-sm" data-testid="storefront-cart-delivery">
                  <span>Delivery</span>
                  <span className="tabular-nums">₹{dc.toFixed(0)}</span>
                </div>
              )}
              <div className="flex justify-between font-black border-t border-ink/20 pt-1">
                <span>Total</span>
                <span className="tabular-nums">₹{drawerTotal.toFixed(0)}</span>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button" onClick={() => { setDrawerOpen(false); setCheckoutOpen(true); }}
                className="flex-1 border-2 border-ink bg-coral text-white px-3 py-2 font-black text-sm shadow-brutalSm"
                data-testid="storefront-cart-drawer-checkout"
              >
                Checkout · ₹{drawerTotal.toFixed(0)}
              </button>
              <button
                type="button" onClick={cart.clear}
                className="border-2 border-ink bg-white px-3 py-2 text-xs font-bold"
                data-testid="storefront-cart-drawer-clear"
              >
                Clear
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
      {checkoutOpen && (
        <CartCheckoutModal
          slug={slug}
          cartList={cart.list}
          buyerUser={effectiveUser}
          requirePhone={requirePhone}
          requireAddress={requireAddress}
          deliveryCharge={dc}
          codEnabled={codEnabled}
          onlineEnabled={onlineEnabled}
          onClose={() => setCheckoutOpen(false)}
          onSuccess={() => {
            setCheckoutOpen(false);
            cart.clear();
          }}
        />
      )}
    </>
  );
}
